import { RouteName } from '@/router'
import { localStorageService } from '@/service/storage.service'

/**
 * 자동 이동이 발생하지 않아야 하는 경로(prefix) 목록 (하위 경로 포함)
 */
const DEFAULT_EXCLUDE_PATHS = [
  RouteName.main,
  RouteName.login,
  RouteName.makeWorksheet,
  RouteName.makeCustomWorkbook,
  RouteName.makeSignatureWorkbook,
]

interface Options {
  /**
   * 경과 시간(ms) 기준. 기본값: 5시간(5 * 60 * 60 * 1000 = 18,000,000ms)
   */
  thresholdMs?: number
  /**
   * 자동 이동 후 다시 이동이 허용되는 쿨다운(ms). 기본값: 7일(7 * 24 * 60 * 60 * 1000)
   */
  cooldownMs?: number
  /**
   * thresholdMs 경과 후 복귀 시 실행할 콜백
   */
  onTimeoutReturn: () => void
  /**
   * 현재 경로가 이 prefix 배열로 시작하면 자동 이동/콜백 실행하지 않음 (하위 경로 포함)
   * 기본값: DEFAULT_EXCLUDE_PATHS
   */
  excludePaths?: string[]
}

/**
 * 앱 전체(모든 탭)에서 마지막 사용 시각 기준으로 N시간 경과 시 복귀 시 콜백을 실행하는 effect util
 * 단, 콜백 실행 후 cooldownMs(기본 7일) 동안은 다시 실행되지 않음
 * excludePaths에 포함된 경로(prefix)에서는 콜백 실행하지 않음
 * @param options Options
 * @returns unmount용 cleanup 함수
 */
export function useAppLastActiveTimeoutEffect(options: Options) {
  const {
    thresholdMs = 5 * 60 * 60 * 1000,
    cooldownMs = 7 * 24 * 60 * 60 * 1000,
    onTimeoutReturn,
    excludePaths = DEFAULT_EXCLUDE_PATHS,
  } = options

  // 마지막 활성 시각을 현재로 갱신
  const updateLastActive = () => {
    localStorageService.setItem('APP_LAST_ACTIVE_TIMESTAMP')(Date.now())
  }

  // 복귀 시 체크
  const handleReturn = () => {
    const lastActive = localStorageService.getItem('APP_LAST_ACTIVE_TIMESTAMP') as number | null
    const lastAutoRedirect = localStorageService.getItem('APP_LAST_AUTO_REDIRECT_TIMESTAMP') as
      | number
      | null
    const now = Date.now()
    const currentPath = window.location.pathname

    const isTimeout = !!lastActive && now - lastActive > thresholdMs
    const isCooldown = !!lastAutoRedirect && now - lastAutoRedirect < cooldownMs
    const isExcluded = excludePaths.some((prefix) => currentPath.startsWith(prefix))

    if (isTimeout && !isCooldown && !isExcluded) {
      localStorageService.setItem('APP_LAST_AUTO_REDIRECT_TIMESTAMP')(now)
      onTimeoutReturn()
      return
    }
    // 활동이 있으면 갱신
    updateLastActive()
  }

  // 여러 탭 동기화(localStorage 변경 감지)
  const handleStorage = (e: StorageEvent) => {
    if (e.key === 'APP_LAST_ACTIVE_TIMESTAMP' && document.visibilityState === 'visible') {
      handleReturn()
    }
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') handleReturn()
  }

  window.addEventListener('blur', updateLastActive)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('focus', handleReturn)
  window.addEventListener('storage', handleStorage)

  // 최초 마운트 시에도 활성화 시각 갱신
  updateLastActive()

  return () => {
    window.removeEventListener('blur', updateLastActive)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('focus', handleReturn)
    window.removeEventListener('storage', handleStorage)
  }
}
